import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetProfitAndLossResponse } from '@/api/handlers/resources/statements/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { StatementComparative } from '@/api/models';
import { StatementQueryKeys } from './statementQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * The result over a period. Keyed on the range, `locale` and `comparative`, so
 * changing the range (or a preset, or the language) refetches; `keepPreviousData`
 * keeps the prior statement on screen while the new one loads.
 */
export const useProfitAndLossQuery = (
    fromDate: string,
    toDate: string,
    locale: string,
    comparative: StatementComparative,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetProfitAndLossResponse>>({
        queryKey: StatementQueryKeys.profitAndLoss(
            fromDate,
            toDate,
            locale,
            comparative,
        ),
        queryFn: () =>
            apiClient.statements.getProfitAndLoss(fromDate, toDate, {
                locale,
                comparative,
            }),
        enabled: fromDate.length > 0 && toDate.length > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};
